// src/hooks/useCascadingLocations.js
import { useState, useEffect, useRef } from "react";
import { standardUserApi } from "../services/standardUserApi"; // or standardUserApi if it’s a user route

/**
 * A custom hook to handle cascading (Country -> City -> Area) from DB data.
 * We'll fetch all (country, city, area) rows from `/admin/locations-rows`.
 */
export function useCascadingLocations(token) {
  const [allLocations, setAllLocations] = useState([]); // store entire table
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fetchedRef = useRef(false);

  useEffect(() => {

    if (fetchedRef.current) return; // only fetch once

    async function fetchLocations() {
      try {
        setLoading(true);
        setError("");
        // We'll define getAllLocationsRows in adminApi (see below)
        const locs = await standardUserApi.getLocationsForCascadingDropdowns();
        setAllLocations(locs); // array of {country, city, area}
      } catch (err) {
        console.error("Error fetching location data:", err);
        setError(err.message || "Failed to load location data");
      } finally {
        setLoading(false);
        fetchedRef.current = true;
      }
    }
    fetchLocations();
  }, [token]);

  /**
   * getCountries
   *  -> returns sorted unique country names from allLocations
   */
  function getCountries() {
    const setOfCountries = new Set(allLocations.map((loc) => loc.country));
    return [...setOfCountries].sort();
  }

  /**
   * getCitiesForCountry
   *  -> returns sorted unique city names for the given country
   */
  function getCitiesForCountry(country) {
    if (!country) return [];
    const subset = allLocations.filter((loc) => loc.country === country);
    const citySet = new Set(subset.map((loc) => loc.city));
    return [...citySet].sort();
  }

  /**
   * getAreasForCountryCity
   *  -> returns sorted unique areas for the given (country, city)
   */
  function getAreasForCountryCity(country, city) {
    if (!country || !city) return [];
    const subset = allLocations.filter(
      (loc) => loc.country === country && loc.city === city
    );
    const areaSet = new Set(subset.map((loc) => loc.area));
    return [...areaSet].sort();
  }

  return {
    loading,
    error,
    getCountries,
    getCitiesForCountry,
    getAreasForCountryCity,
  };
}
